import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";

import { securityCheck, upsertLoginSession } from "../common/security/api";
import { getFingerprint } from "../common/security/fingerprint";
import { renderRecaptcha, resetRecaptcha } from "../common/security/capyClient";
import { setSessionId } from "../common/security/session";
import { buildVisibleProjects } from "../utils/projectVisibility";

function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const [needCapy, setNeedCapy] = useState(false);
  const captchaWidgetId = useRef(null);
  const captchaContainerId = "recaptcha-box";

  useEffect(() => {
    setErr("");
  }, [email, password]);

  async function doLogin(e) {
    e.preventDefault();
    if (loading) return;

    setErr("");
    setLoading(true);

    const fingerprintPromise = getFingerprint();
    const deviceInfo = navigator.userAgent || "-";
    const deviceType = window.innerWidth < 768 ? "Mobil" : "Masaüstü";

    let token = null;

    if (needCapy) {
      if (window.grecaptcha && typeof window.grecaptcha.getResponse === "function") {
        token = window.grecaptcha.getResponse(captchaWidgetId.current);
      }
      if (!token) {
        setErr("Lütfen 'Ben robot değilim' kutusunu işaretleyin.");
        setLoading(false);
        return;
      }
    }

    const fingerprint = await fingerprintPromise;

    const check = await securityCheck({
      path: "/login",
      action: "login",
      fingerprint,
      deviceInfo,
      deviceType,
      token,
    });

    if (check.require === "capy") {
      setNeedCapy(true);

      if (!captchaWidgetId.current) {
        try {
          captchaWidgetId.current = await renderRecaptcha(captchaContainerId, {
            callback: () => {},
            "error-callback": () => setErr("reCAPTCHA yüklenemedi."),
          });
        } catch {
          setErr("reCAPTCHA yüklenemedi.");
        }
      } else {
        resetRecaptcha(captchaWidgetId.current);
      }

      setLoading(false);
      return;
    }

    if (!check.ok) {
      setErr("Güvenlik doğrulaması başarısız.");
      setLoading(false);
      return;
    }

    try {
      await signInWithEmailAndPassword(auth, email, password);

      const snap = await getDoc(doc(db, "users", email));
      const userData = snap.exists() ? snap.data() || {} : {};
      const role = userData.role || "Kullanıcı";

      const up = await upsertLoginSession({
        email,
        fingerprint,
        deviceInfo,
        deviceType,
      });

      if (up.ok && up.sessionId) setSessionId(up.sessionId);

      const visibleProjects = buildVisibleProjects({
        role,
        userPermissions: {
          yetkiKontrol: userData.yetkiKontrol === true || userData.YetkiKontrol === true,
          YetkiKontrol: userData.YetkiKontrol === true || userData.yetkiKontrol === true,
          ...(userData.fatura || {}),
          aractakip: userData.aractakip || userData["arac-takip"] || {},
          "arac-takip": userData["arac-takip"] || userData.aractakip || {},
          kazatakip: userData.kazatakip || {},
          filotakip: userData.filotakip || {},
          personeltakip: userData.personeltakip || {},
          mazottakip: userData.mazottakip || {},
          "mazot-takip": userData["mazot-takip"] || {},
          "hazir-beton": userData["hazir-beton"] || {},
          "cimento-operasyonu": userData["cimento-operasyonu"] || {},
          "kisiye-ozel-rapor": userData["kisiye-ozel-rapor"] || {},
          "kontrol-formlari": userData["kontrol-formlari"] || {},
        },
      });

      if (visibleProjects.length === 1) {
        navigate(visibleProjects[0].path);
      } else {
        navigate("/secilen-proje", { state: { role } });
      }
    } catch {
      setErr("Giriş başarısız! Bilgilerinizi kontrol edin.");
    }

    setLoading(false);
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#f1f5f9",
        fontFamily: "Arial, sans-serif",
      }}
    >
      <form
        onSubmit={doLogin}
        style={{
          backgroundColor: "white",
          padding: 30,
          borderRadius: 8,
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          display: "flex",
          flexDirection: "column",
          gap: 14,
          width: 320,
        }}
      >
        <h2 style={{ textAlign: "center", color: "#1e293b" }}>Giriş Yap</h2>

        <input
          type="email"
          placeholder="E-posta"
          value={email}
          disabled={loading}
          onChange={(e) => setEmail(e.target.value)}
          required
          style={{ padding: 10, border: "1px solid #cbd5e1", borderRadius: 6 }}
        />

        <input
          type="password"
          placeholder="Şifre"
          value={password}
          disabled={loading}
          onChange={(e) => setPassword(e.target.value)}
          required
          style={{ padding: 10, border: "1px solid #cbd5e1", borderRadius: 6 }}
        />

        {needCapy && (
          <div style={{ marginTop: 4 }}>
            <div id={captchaContainerId} />
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          style={{
            marginTop: needCapy ? 8 : 4,
            backgroundColor: loading ? "#94a3b8" : "#2563eb",
            color: "white",
            padding: "10px",
            border: "none",
            borderRadius: 6,
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
        </button>

        {err && (
          <p style={{ color: "red", fontSize: 13, textAlign: "center" }}>{err}</p>
        )}
      </form>
    </div>
  );
}

export default Login;
